import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const IMG = {
  hero: "https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMGNhciUyMG5pZ2h0JTIwY2hhdWZmZXVyfGVufDB8fHx8MTc4NzM3ODYwNnww&ixlib=rb-4.1.0&q=85",
};

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function HomeHero() {
  return (
    <section
      id="hero"
      data-testid="homepage-hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-near-black"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Chauffeured black sedan on a Sydney street at night"
          className="w-full h-full object-cover sjc-slow-reveal"
          loading="eager"
        />
        {/* Cinematic overlays — bottom fade + strong left-side vignette for copy legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-near-black/60 via-transparent to-near-black" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,5,7,0.95) 0%, rgba(3,5,7,0.85) 25%, rgba(3,5,7,0.55) 50%, rgba(3,5,7,0.15) 75%, rgba(3,5,7,0) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-[92rem] mx-auto px-6 lg:px-12 pb-20 md:pb-28 pt-40">
        <div className="max-w-3xl">
          <motion.p
            {...fadeUp}
            data-testid="hero-eyebrow"
            className="sjc-eyebrow text-warm-white/70 flex items-center gap-4"
          >
            <span className="sjc-hairline w-10" />
            Sydney Private Chauffeur Service
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            data-testid="hero-main-heading"
            className="font-display mt-8 text-warm-white text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[1.02] tracking-[-0.02em] font-normal"
          >
            Private travel,
            <br />
            <span className="italic font-light">precisely handled.</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            data-testid="hero-subheading"
            className="mt-8 max-w-xl text-warm-white/70 text-base md:text-lg font-light leading-relaxed"
          >
            Discreet, reliable chauffeured transfers across Sydney, its airports and surrounding regions — quietly delivered by career drivers who know the city intimately.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8"
          >
            <Link
              to="/book"
              data-testid="hero-primary-cta-book"
              className="group relative inline-flex items-center gap-3 bg-warm-white text-midnight px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 hover:bg-gold"
            >
              <span>Book Your Journey</span>
              <span className="w-2 h-2 rounded-full bg-midnight transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/fleet"
              data-testid="hero-secondary-cta-fleet"
              className="group inline-flex items-center gap-3 text-warm-white/85 hover:text-warm-white text-[11px] tracking-[0.28em] uppercase font-medium py-2 transition-colors duration-300"
            >
              <span>Explore Our Fleet</span>
              <span className="w-8 h-px bg-gold transition-all duration-500 group-hover:w-12" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Faint corner mark — restrained luxury detail */}
      <div className="absolute top-40 right-8 md:right-12 hidden md:flex flex-col items-end gap-4 z-10">
        <span className="sjc-eyebrow text-warm-white/40">Est. 2012</span>
        <span className="sjc-hairline w-10 opacity-40" />
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <span className="sjc-eyebrow text-warm-white/60">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-warm-white/45 to-transparent" />
      </div>
    </section>
  );
}
